<template>
  <div class="activity-detail" v-loading="loading">
    <template v-if="detail.id">
      <el-image :src="detail.cover_img || '/static/images/logo.jpg'" fit="cover" class="hero" />
      <div class="main">
        <h1>{{ detail.title }}</h1>
        <div class="meta">
          <span>{{ detail.address || '地点待定' }}</span>
          <span>{{ detail.member_only === 1 ? '仅会员可报名' : '注册用户可报名' }}</span>
        </div>

        <div class="time-list">
          <div class="time-row">
            <span class="time-label">活动</span>
            <span class="time-value">{{ formatTimeRange(detail.start_time, detail.end_time) }}</span>
          </div>
          <div class="time-row">
            <span class="time-label">报名</span>
            <span class="time-value">{{ formatOptionalRange(detail.signup_start_time, detail.signup_end_time, '报名时间不限') }}</span>
          </div>
          <div class="time-row">
            <span class="time-label">签到</span>
            <span class="time-value">{{ formatCheckinRange(detail) }}</span>
          </div>
        </div>

        <div class="buy">
          <div><span class="price">¥{{ detail.price }}</span><span class="unit">/人</span></div>
          <el-button type="primary" @click="openSignup">立即报名</el-button>
        </div>
        <div class="content" v-html="detail.content"></div>
      </div>
    </template>

    <el-dialog v-model="signupVisible" title="活动报名" width="560px" :close-on-click-modal="false" @close="resetSignupForm">
      <el-form ref="signupFormRef" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="联系人" prop="contact_name">
          <el-input v-model="form.contact_name" maxlength="30" />
        </el-form-item>
        <el-form-item label="手机号" prop="contact_mobile">
          <el-input v-model="form.contact_mobile" maxlength="11" />
        </el-form-item>
        <el-form-item label="单位" prop="contact_company">
          <el-input v-model="form.contact_company" maxlength="60" />
        </el-form-item>
        <el-form-item label="报名人数" prop="quantity">
          <el-input-number v-model="form.quantity" :min="1" :max="maxQuantity" />
        </el-form-item>
        <el-form-item label="支付方式" prop="payChannel">
          <div class="payment-methods">
            <div class="payment-item" :class="{ active: form.payChannel === 'wechat' }" @click="selectPaymentMethod('wechat')">
              <img src="@/assets/images/weixin.png" alt="微信" class="payment-icon" />
              <span class="payment-name">微信支付</span>
            </div>
            <div class="payment-item" :class="{ active: form.payChannel === 'alipay' }" @click="selectPaymentMethod('alipay')">
              <img src="@/assets/images/alipay.png" alt="支付宝" class="payment-icon" />
              <span class="payment-name">支付宝支付</span>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="应付金额">
          <strong class="amount">¥{{ totalAmount }}</strong>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="signupVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitOrder">提交信息并支付</el-button>
      </template>
    </el-dialog>

    <div v-if="showWechatQrcode" class="wechat-pay-overlay">
      <div class="wechat-pay-panel">
        <button class="wechat-pay-close" type="button" @click="cancelWechatPayment">×</button>
        <div class="wechat-dialog-header">
          <div class="wechat-dialog-icon">
            <img src="@/assets/images/weixin.png" alt="微信支付" />
          </div>
          <div class="wechat-dialog-title-wrap">
            <div class="wechat-dialog-title">微信扫码支付</div>
            <div class="wechat-dialog-subtitle">请在微信中完成活动报名支付</div>
          </div>
        </div>

        <div class="qrcode-content">
          <div class="qrcode-wrapper">
            <div v-if="wechatQrcodeUrl" :key="qrcodeKey" ref="qrcodeRef" class="qrcode-canvas"></div>
            <div v-else class="qrcode-loading">
              <p>二维码生成中...</p>
            </div>
          </div>
          <div class="qrcode-amount">¥{{ currentPayAmount }}</div>
          <div class="qrcode-tips">
            <p>使用微信扫一扫完成支付，支付成功后点击下方按钮查看订单</p>
          </div>
        </div>

        <div class="qrcode-actions">
          <el-button @click="cancelWechatPayment">取消支付</el-button>
          <el-button type="primary" :loading="checkingPayment" @click="handleWechatPaymentComplete">已完成支付</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import activity from '@/api/activity'
import dayjs from 'dayjs'
import QRCode from 'qrcodejs2-fix'

export default {
  name: 'activityDetail',
  data() {
    return {
      loading: false,
      signupVisible: false,
      submitting: false,
      checkingPayment: false,
      showWechatQrcode: false,
      wechatQrcodeUrl: '',
      qrcodeKey: 0,
      currentOrderNo: '',
      currentPayAmount: '0.00',
      detail: {},
      form: {
        contact_name: '',
        contact_mobile: '',
        contact_company: '',
        quantity: 1,
        payChannel: 'wechat'
      },
      rules: {
        contact_name: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
        contact_mobile: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1\d{10}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        quantity: [{ required: true, message: '请输入报名人数', trigger: 'change' }],
        payChannel: [{ required: true, message: '请选择支付方式', trigger: 'change' }]
      }
    }
  },
  computed: {
    maxQuantity() {
      return this.detail.total_quota > 0 ? Math.max(1, Number(this.detail.remaining_quota || 0)) : 999
    },
    totalAmount() {
      return (Number(this.detail.price || 0) * Number(this.form.quantity || 1)).toFixed(2)
    }
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await activity.detail(this.$route.params.id)
        if (res) {
          this.detail = res.data || {}
        }
      } finally {
        this.loading = false
      }
    },
    openSignup() {
      if (this.detail.total_quota > 0 && Number(this.detail.remaining_quota || 0) <= 0) {
        this.$message.warning('当前活动名额已满')
        return
      }
      this.signupVisible = true
    },
    selectPaymentMethod(method) {
      this.form.payChannel = method
      if (this.$refs.signupFormRef) {
        this.$refs.signupFormRef.validateField('payChannel')
      }
    },
    resetSignupForm() {
      this.submitting = false
      this.form = {
        contact_name: '',
        contact_mobile: '',
        contact_company: '',
        quantity: 1,
        payChannel: 'wechat'
      }
      this.$nextTick(() => {
        this.$refs.signupFormRef && this.$refs.signupFormRef.clearValidate()
      })
    },
    formatTime(value) {
      if (!value) return ''
      return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD HH:mm') : value
    },
    formatTimeRange(start, end) {
      if (!start && !end) return '时间待定'
      if (!start) return `至 ${this.formatTime(end)}`
      if (!end) return `${this.formatTime(start)} 起`

      const startTime = dayjs(start)
      const endTime = dayjs(end)
      if (!startTime.isValid() || !endTime.isValid()) {
        return `${start} 至 ${end}`
      }
      if (startTime.format('YYYY-MM-DD') === endTime.format('YYYY-MM-DD')) {
        return `${startTime.format('YYYY-MM-DD HH:mm')} - ${endTime.format('HH:mm')}`
      }
      return `${startTime.format('YYYY-MM-DD HH:mm')} 至 ${endTime.format('YYYY-MM-DD HH:mm')}`
    },
    formatOptionalRange(start, end, emptyText) {
      if (!start && !end) return emptyText
      return this.formatTimeRange(start, end)
    },
    formatCheckinRange(item) {
      if (item.checkin_start_time || item.checkin_end_time) {
        return this.formatTimeRange(item.checkin_start_time, item.checkin_end_time)
      }
      if (item.start_time && item.end_time) {
        const day = dayjs(item.start_time)
        const end = dayjs(item.end_time)
        if (day.isValid() && end.isValid()) {
          return `${day.format('YYYY-MM-DD')} 当天至 ${end.format('HH:mm')}`
        }
      }
      return '按活动时间签到'
    },
    extractPayData(data) {
      if (!data) return ''
      if (typeof data === 'string') return data
      return data.payData || data.code_url || data.qr_code || data.url || ''
    },
    generateQRCode(url) {
      if (!this.showWechatQrcode) return
      const container = this.$refs.qrcodeRef
      if (!container) {
        setTimeout(() => this.generateQRCode(url), 100)
        return
      }
      container.innerHTML = ''
      new QRCode(container, {
        text: url,
        width: 240,
        height: 240,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      })
    },
    resetWechatPayment() {
      this.showWechatQrcode = false
      this.wechatQrcodeUrl = ''
      this.currentOrderNo = ''
      this.currentPayAmount = '0.00'
      this.checkingPayment = false
      this.qrcodeKey += 1
    },
    cancelWechatPayment() {
      this.resetWechatPayment()
      this.$message.info('已取消支付')
    },
    isPaidResult(res) {
      const data = (res && res.data) || {}
      const statusList = [data.pay_status, data.status, data.state, data.payment_status, data.order_status]
      if (statusList.some(item => Number(item) === 1)) return true
      return data.paid === true || data.is_paid === true
    },
    async checkOrderPaidStatus(showPendingMessage = true) {
      if (!this.currentOrderNo) {
        this.$message.warning('订单号缺失，请前往我的订单查看支付状态')
        this.$router.push('/activity/my-orders')
        return false
      }
      this.checkingPayment = true
      try {
        const res = await activity.payStatus(this.currentOrderNo)
        const paid = this.isPaidResult(res)
        if (paid) {
          this.resetWechatPayment()
          this.$message.success('支付成功，正在跳转到我的订单')
          this.$router.push('/activity/my-orders')
          return true
        }
        if (showPendingMessage) {
          this.$message.warning('暂未查询到支付成功，请完成支付后再试，或稍后到我的订单查看')
        }
        return false
      } catch (e) {
        console.error('查询活动订单支付状态失败:', e)
        this.$message.error('支付状态查询失败，请稍后到我的订单查看')
        return false
      } finally {
        this.checkingPayment = false
      }
    },
    async handleWechatPaymentComplete() {
      await this.checkOrderPaidStatus(true)
    },
    async submitOrder() {
      if (!this.$refs.signupFormRef) return
      try {
        await this.$refs.signupFormRef.validate()
      } catch (e) {
        return
      }

      this.submitting = true
      try {
        const payment_type = this.form.payChannel === 'wechat' ? 1 : 2
        const payment_method = this.form.payChannel === 'wechat' ? 'native' : 'page'
        const res = await activity.createOrder({
          ...this.form,
          activity_id: this.detail.id,
          payment_type,
          payment_method,
          return_url: window.location.origin + '/activity/my-orders'
        })

        if (!res || res.code !== 2000) {
          this.$message.error((res && res.msg) || '创建报名订单失败')
          return
        }

        const payData = res.data && res.data.pay_data
        const orderData = res.data && res.data.order
        const payUrl = this.extractPayData(payData)
        this.currentOrderNo = (
          (orderData && (orderData.order_no || orderData.order_sn || orderData.orderNo)) ||
          (res.data && (res.data.order_no || res.data.order_sn || res.data.orderNo)) ||
          ''
        )
        this.currentPayAmount = String(
          (orderData && (orderData.pay_amount || orderData.amount)) ||
          this.totalAmount
        )
        this.signupVisible = false

        if (this.form.payChannel === 'wechat') {
          if (!payUrl) {
            this.$message.error('获取微信支付二维码失败')
            return
          }
          this.showWechatQrcode = true
          this.wechatQrcodeUrl = payUrl
          this.qrcodeKey += 1
          this.$message.success('报名信息已提交，请扫码完成支付')
          await this.$nextTick()
          await this.$nextTick()
          this.generateQRCode(payUrl)
          return
        }

        if (!payUrl) {
          this.$message.error('获取支付宝支付链接失败')
          return
        }

        window.open(payUrl, '_blank')
        this.$confirm('请在新窗口完成支付，支付完成后点击“已完成支付”查看订单状态', '支付提示', {
          confirmButtonText: '已完成支付',
          cancelButtonText: '稍后支付',
          type: 'info',
          closeOnClickModal: false
        }).then(async () => {
          const paid = await this.checkOrderPaidStatus(false)
          if (!paid) {
            this.$message.warning('暂未查询到支付成功，订单已保留，可稍后在我的订单继续查看')
            this.$router.push('/activity/my-orders')
          }
        }).catch(() => {
          this.$message.info('订单已创建，可稍后在我的订单中继续支付')
          this.$router.push('/activity/my-orders')
        })
      } catch (e) {
        console.error('创建活动报名订单失败:', e)
        this.$message.error('创建报名订单失败，请稍后重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.activity-detail {
  padding: 30px;
}

.hero {
  width: 100%;
  height: 280px;
  border-radius: 8px;
}

.main {
  padding: 24px 0;
}

.main h1 {
  margin: 0 0 14px;
  color: #222;
  font-size: 24px;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
  color: #666;
}

.time-list {
  max-width: 760px;
  margin-bottom: 22px;
  padding: 14px 16px;
  background: #f8fafd;
  border: 1px solid #edf0f5;
  border-radius: 8px;
}

.time-row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  color: #5f6673;
  font-size: 14px;
  line-height: 1.5;
}

.time-row + .time-row {
  margin-top: 8px;
}

.time-label {
  height: 22px;
  color: #409eff;
  font-size: 13px;
  line-height: 22px;
  text-align: center;
  background: #ecf5ff;
  border-radius: 4px;
}

.time-value {
  min-width: 0;
  word-break: break-word;
}

.buy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 16px 20px;
  border: 1px solid #edf0f5;
  border-radius: 8px;
}

.price {
  color: #f56c6c;
  font-size: 28px;
  font-weight: 700;
}

.unit {
  margin-left: 4px;
  color: #909399;
}

.content {
  color: #333;
  line-height: 1.8;
}

.payment-methods {
  display: flex;
  gap: 16px;
  width: 100%;
}

.payment-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}

.payment-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
}

.payment-item.active {
  border-color: #409eff;
  background: #ecf5ff;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.2);
}

.payment-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.payment-name {
  color: #303133;
  font-size: 15px;
  font-weight: 500;
}

.amount {
  color: #f56c6c;
  font-size: 22px;
}

.wechat-pay-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.52);
  box-sizing: border-box;
}

.wechat-pay-panel {
  position: relative;
  width: 430px;
  max-width: calc(100vw - 32px);
  overflow: hidden;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.22);
}

.wechat-pay-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #909399;
  font-size: 28px;
  line-height: 32px;
  cursor: pointer;
}

.wechat-pay-close:hover {
  background: #f5f7fa;
  color: #606266;
}

.wechat-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 22px 58px 18px 28px;
  border-bottom: 1px solid #edf0f5;
  white-space: nowrap;
}

.wechat-dialog-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e9f8ef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
}

.wechat-dialog-icon img {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.wechat-dialog-title-wrap {
  min-width: 0;
}

.wechat-dialog-title {
  font-size: 19px;
  line-height: 1.3;
  font-weight: 600;
  color: #1f2d3d;
  white-space: nowrap;
}

.wechat-dialog-subtitle {
  margin-top: 3px;
  font-size: 12px;
  line-height: 1.35;
  color: #8a95a3;
  white-space: nowrap;
}

.qrcode-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 28px 8px;
  background: linear-gradient(180deg, #f8fbff 0%, #fff 42%);
  text-align: center;
}

.qrcode-wrapper {
  width: 284px;
  height: 284px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  padding: 22px;
  background-color: #fff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-sizing: border-box;
  box-shadow: 0 10px 30px rgba(31, 45, 61, 0.08);
}

.qrcode-canvas {
  width: 240px;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  overflow: hidden;
}

.qrcode-canvas :deep(canvas),
.qrcode-canvas :deep(img) {
  display: block;
  width: 240px !important;
  height: 240px !important;
}

.qrcode-loading {
  text-align: center;
  color: #909399;
}

.qrcode-loading p {
  margin: 0;
  font-size: 14px;
}

.qrcode-amount {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  margin: 0 auto 10px;
  padding: 4px 14px;
  border-radius: 999px;
  background: #fff4f0;
  color: #f05a28;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
}

.qrcode-tips p {
  margin: 0;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
}

.qrcode-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 14px 28px 26px;
}

.qrcode-actions .el-button {
  min-width: 120px;
  height: 40px;
  border-radius: 6px;
  font-size: 15px;
}

@media (max-width: 768px) {
  .activity-detail {
    padding: 20px 12px;
  }

  .buy {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .payment-methods {
    flex-direction: column;
  }

  .wechat-pay-overlay {
    padding: 16px;
  }

  .wechat-pay-panel {
    width: 100%;
  }

  .wechat-dialog-header {
    padding: 18px 56px 16px 20px;
  }

  .wechat-dialog-title {
    font-size: 17px;
  }

  .qrcode-content {
    padding: 22px 18px 8px;
  }

  .qrcode-wrapper {
    width: min(260px, calc(100vw - 76px));
    height: min(260px, calc(100vw - 76px));
    padding: 16px;
  }

  .qrcode-canvas {
    width: 228px;
    height: 228px;
  }

  .qrcode-canvas :deep(canvas),
.qrcode-canvas :deep(img) {
    width: 228px !important;
    height: 228px !important;
  }

  .qrcode-actions {
    padding: 12px 18px 22px;
  }

  .qrcode-actions .el-button {
    min-width: 110px;
  }
}
</style>
